"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import type { LucideIcon } from "lucide-react"
import {
  ArrowRight,
  Bug,
  Calculator,
  ChevronDown,
  Droplet,
  Flag,
  FlaskConical,
  Gem,
  Leaf,
  MessageCircle,
  Plus,
  ScanLine,
  Scissors,
  Search,
  Sprout,
  User,
  X,
} from "lucide-react"
import Link from "next/link"
import { motion, MotionConfig } from "motion/react"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { trackEvent } from "@/lib/analytics"
import { SearchHighlight } from "@/components/ui/search-highlight"

const ROW_STAGGER_STEP = 0.025
const ROW_MAX_STAGGER = 0.18

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  All: Flag,
  "About What Grass Is This?": Sprout,
  "Pricing & Premium": Gem,
  "Tools & Calculators": Calculator,
  "Using the Grass Identifier": ScanLine,
  "Accounts & Settings": User,
  "Getting Started": Flag,
  Watering: Droplet,
  Mowing: Scissors,
  "Fertilizing & Soil Health": FlaskConical,
  "Seeding & Establishment": Leaf,
  "Weeds, Pests & Problems": Bug,
}

interface FAQItem {
  id: string
  question: string
  answer: string
  category: string
  order: number
}

interface FAQClientProps {
  faqData: FAQItem[]
  categories: string[]
}

export function FAQClient({ faqData, categories }: FAQClientProps) {
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("All")

  const filteredFAQs = useMemo(() => faqData.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase())
      || faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === "All" || faq.category === selectedCategory
    return matchesSearch && matchesCategory
  }), [faqData, searchTerm, selectedCategory])

  const groupedFAQs = useMemo(() => {
    const groups: Record<string, FAQItem[]> = {}
    for (const category of categories) {
      groups[category] = []
    }
    for (const faq of filteredFAQs) {
      if (!groups[faq.category]) groups[faq.category] = []
      groups[faq.category].push(faq)
    }
    return groups
  }, [categories, filteredFAQs])

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { All: faqData.length }
    for (const faq of faqData) {
      counts[faq.category] = (counts[faq.category] || 0) + 1
    }
    return counts
  }, [faqData])

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!searchTerm) return
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      trackEvent("faq_searched", {
        query_length: searchTerm.length,
        result_count: filteredFAQs.length,
      })
    }, 600)
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    }
  }, [searchTerm, filteredFAQs.length])

  const handleSearch = (value: string) => {
    setSearchTerm(value)
  }

  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category)
  }

  const clearFilters = () => {
    setSearchTerm("")
    setSelectedCategory("All")
  }

  const toggleItem = (id: string) => {
    const isExpanding = !expandedItems.includes(id)
    setExpandedItems(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    )
    if (isExpanding) {
      const faq = faqData.find(f => f.id === id)
      trackEvent("faq_expanded", { question: faq?.question ?? id })
      setTimeout(() => {
        const element = document.getElementById(`faq-row-${id}`)
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest"
          })
        }
      }, 100)
    }
  }

  const showNoResults = (searchTerm || selectedCategory !== "All") && filteredFAQs.length === 0
  const isFlatView = Boolean(searchTerm) || selectedCategory !== "All"
  const allCategories = ["All", ...categories]

  return (
    <MotionConfig reducedMotion="user">
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        id="faq-search-results"
      >
        {searchTerm && filteredFAQs.length > 0 &&
          `Found ${filteredFAQs.length} answer${filteredFAQs.length === 1 ? "" : "s"} for "${searchTerm}"`}
        {searchTerm && filteredFAQs.length === 0 &&
          `No answers found for "${searchTerm}"`}
        {!searchTerm && filteredFAQs.length > 0 &&
          `Showing ${filteredFAQs.length} frequently asked question${filteredFAQs.length === 1 ? "" : "s"}`}
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        <div>
          <div className="mb-8 md:mb-10">
            <div className="ds-glass relative mb-4 max-w-2xl rounded-full">
              <Search className="pointer-events-none absolute left-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-green-700" />
              <input
                type="search"
                inputMode="search"
                placeholder={`Search ${faqData.length} questions`}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="relative z-10 w-full rounded-full bg-transparent py-3.5 pl-11 pr-12 text-base text-gray-900 placeholder:text-gray-500 focus:outline-2 focus:outline-offset-2 focus:outline-green-500 md:text-[15px]"
                aria-label="Search frequently asked questions"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => handleSearch("")}
                  className="absolute right-2 top-1/2 z-10 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/60 bg-white/70 text-gray-600 shadow-sm backdrop-blur-sm transition-colors hover:bg-white hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="ds-glass relative rounded-xl md:hidden">
              <label htmlFor="faq-category" className="sr-only">
                Filter questions by category
              </label>
              <select
                id="faq-category"
                value={selectedCategory}
                onChange={(event) => handleCategoryFilter(event.target.value)}
                className="min-h-[48px] w-full appearance-none rounded-xl border border-white/70 bg-white/70 px-4 py-3 pr-11 text-sm font-semibold text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2"
              >
                {allCategories.map((category) => (
                  <option key={category} value={category}>
                    {category} ({categoryCounts[category] ?? 0})
                  </option>
                ))}
              </select>
              <ChevronDown
                className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-green-700"
                aria-hidden="true"
              />
            </div>

            <div className="relative hidden md:block">
              <div
                role="group"
                aria-label="Filter questions by category"
                className="flex flex-wrap gap-2"
              >
                {allCategories.map((cat) => {
                  const isActive = selectedCategory === cat
                  const count = categoryCounts[cat] ?? 0
                  const Icon = CATEGORY_ICONS[cat] ?? Flag
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => handleCategoryFilter(cat)}
                      className={`flex-shrink-0 snap-start inline-flex min-h-[44px] items-center gap-2 rounded-full border px-3.5 py-2 text-sm leading-tight transition-colors ${
                        isActive
                          ? "border-transparent bg-linear-to-r from-green-600 to-emerald-600 font-semibold text-white shadow-[0_12px_26px_-8px_rgba(22,163,74,0.5)]"
                          : "border-white/60 bg-white/60 font-medium text-gray-700 backdrop-blur-sm hover:border-white/85 hover:bg-white/75 hover:text-green-700"
                      }`}
                      aria-pressed={isActive}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                      <span>{cat}</span>
                      <span className={`min-w-[18px] rounded-full px-1.5 py-px text-center text-xs font-semibold ${
                        isActive ? "bg-white/20 text-white" : "bg-green-50/80 text-green-700"
                      }`}>
                        {count}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {showNoResults && (
            <motion.div
              {...FADE_UP} transition={FADE_TRANSITION}
              className="ds-glass mb-12 rounded-[20px] px-6 py-12 text-center md:py-16"
            >
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-green-700">
                <Search className="h-5 w-5" aria-hidden="true" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                No matching questions
              </h2>
              <p className="mx-auto mt-2 mb-5 max-w-md text-sm leading-relaxed text-gray-600">
                Try a different word, or browse all questions.
              </p>
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-green-200 bg-white px-5 py-2.5 text-sm font-semibold text-green-700 transition-colors hover:bg-green-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500"
              >
                Clear search
              </button>
            </motion.div>
          )}

          {!showNoResults && isFlatView && (
            <motion.div
              {...FADE_UP} transition={FADE_TRANSITION}
              className="mb-12 space-y-3 md:mb-16"
            >
              {filteredFAQs.map((faq, index) => (
                <FAQAccordionItem
                  key={faq.id}
                  faq={faq}
                  index={index}
                  isOpen={expandedItems.includes(faq.id)}
                  searchTerm={searchTerm}
                  onToggle={toggleItem}
                />
              ))}
            </motion.div>
          )}

          {!showNoResults && !isFlatView && (
            <div className="mb-12 space-y-9 md:mb-16">
              {categories.map((category) => {
                const items = groupedFAQs[category] ?? []
                if (items.length === 0) return null
                const Icon = CATEGORY_ICONS[category] ?? Flag

                return (
                  <motion.section
                    key={category}
                    {...FADE_UP}
                    transition={FADE_TRANSITION}
                    className="space-y-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[18px] border border-green-200/70 bg-green-50/55 text-green-700 backdrop-blur-sm">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold leading-tight text-gray-900">
                          {category}
                        </h2>
                        <p className="text-sm text-gray-600">
                          {items.length} question{items.length === 1 ? "" : "s"}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {items.map((faq, index) => (
                        <FAQAccordionItem
                          key={faq.id}
                          faq={faq}
                          index={index}
                          isOpen={expandedItems.includes(faq.id)}
                          searchTerm={searchTerm}
                          onToggle={toggleItem}
                        />
                      ))}
                    </div>
                  </motion.section>
                )
              })}
            </div>
          )}

          <motion.section
            {...FADE_UP} transition={{ ...FADE_TRANSITION, delay: 0.1 }}
            className="ds-glass mb-4 mt-10 rounded-[20px] px-6 py-9 text-center md:mt-16 md:px-8 md:py-10"
          >
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-linear-to-r from-green-600 to-emerald-600 text-white shadow-[0_12px_26px_-8px_rgba(22,163,74,0.5)]">
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
            </div>
            <h3 className="text-xl font-bold leading-tight text-gray-900 md:text-2xl">
              Still have a question?
            </h3>
            <p className="mx-auto mt-2 mb-5 max-w-md text-[15px] leading-[1.6] text-gray-600">
              Identify your grass for a personalized plan, or send us a note and a real person will reply within a day.
            </p>
            <div className="flex flex-col items-stretch justify-center gap-2.5 sm:flex-row sm:items-center">
              <Link
                href="/"
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-linear-to-r from-green-600 to-emerald-600 px-5 py-2.5 text-[14.5px] font-semibold text-white shadow-[0_12px_26px_-8px_rgba(22,163,74,0.5)] transition hover:-translate-y-0.5 hover:from-green-700 hover:to-emerald-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-700"
              >
                Identify your grass
                <ArrowRight className="h-4 w-4" strokeWidth={2.2} aria-hidden="true" />
              </Link>
              <Link
                href="/contact"
                className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-green-200 bg-white/60 px-5 py-2.5 text-[14.5px] font-semibold text-green-700 backdrop-blur-sm transition-colors hover:bg-white hover:text-green-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500"
              >
                Contact us
              </Link>
            </div>
          </motion.section>
        </div>
      </div>
    </MotionConfig>
  )
}

function FAQAccordionItem({
  faq,
  index,
  isOpen,
  searchTerm,
  onToggle,
}: {
  faq: FAQItem
  index: number
  isOpen: boolean
  searchTerm: string
  onToggle: (id: string) => void
}) {
  return (
    <motion.div
      id={`faq-row-${faq.id}`}
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{
        ...FADE_TRANSITION,
        delay: Math.min(index * ROW_STAGGER_STEP, ROW_MAX_STAGGER),
      }}
      className="ds-glass rounded-[18px] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5"
    >
      <button
        type="button"
        onClick={() => onToggle(faq.id)}
        className="group flex w-full items-center justify-between gap-4 rounded-[18px] px-5 py-5 text-left focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-green-500 md:gap-6 md:px-6"
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${faq.id}`}
      >
        <span
          id={`faq-question-${faq.id}`}
          className="text-base font-semibold leading-[1.45] text-gray-900 transition-colors group-hover:text-green-800 md:text-[17px]"
        >
          <SearchHighlight
            text={faq.question}
            searchTerm={searchTerm}
            className="text-base font-semibold leading-[1.45] text-gray-900 md:text-[17px]"
          />
        </span>
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-green-200 bg-green-50 text-green-700">
          <Plus
            className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-45" : ""}`}
            strokeWidth={2.2}
            aria-hidden="true"
          />
        </span>
      </button>
      {isOpen && (
        <div
          id={`faq-answer-${faq.id}`}
          role="region"
          aria-labelledby={`faq-question-${faq.id}`}
          className="px-5 pb-5 md:px-6"
        >
          <p className="border-t border-white/55 pt-4 text-base leading-[1.7] text-gray-700 md:pr-12">
            <SearchHighlight
              text={faq.answer}
              searchTerm={searchTerm}
              className="text-base leading-[1.7] text-gray-700"
            />
          </p>
        </div>
      )}
    </motion.div>
  )
}
